import { useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

// Where a record page's active tab lives (BaseForm, the Surety account profile;
// the strip is RecordTabs.tsx).

export interface RecordTabDef {
  id: string;
  label: string;
}

/**
 * The active tab. With `param`, it lives in `?<param>=` so a reload or a deep
 * link reopens it; the FIRST tab is the default and stays out of the URL. An id
 * the page doesn't know (a stale link) falls back to the first tab.
 */
export function useTabParam(
  tabs: readonly RecordTabDef[],
  param?: string,
): [string, (id: string) => void] {
  const [params, setParams] = useSearchParams();
  const { state } = useLocation();
  const [localTab, setLocalTab] = useState(tabs[0]?.id ?? "");
  const wanted = param ? params.get(param) : localTab;
  const active = tabs.find((t) => t.id === wanted)?.id ?? tabs[0]?.id ?? "";

  function select(id: string) {
    if (!param) {
      setLocalTab(id);
      return;
    }
    const next = new URLSearchParams(params);
    if (id === tabs[0]?.id) next.delete(param);
    else next.set(param, id);
    // A replace of the SAME entry, so it keeps the entry's router state: the
    // bond submission's arrival confirmation rides in it (BRK-S399).
    setParams(next, { replace: true, state });
  }

  return [active, select];
}

/** The ids that tie a tab to its panel (`aria-controls` / `aria-labelledby`). */
export const recordTabId = (idBase: string, id: string) =>
  `${idBase}-tab-${id}`;
export const recordPanelId = (idBase: string, id: string) =>
  `${idBase}-panel-${id}`;
